// English mirror of the Swedish photo cluster (/foto → /en/photography).
// Only pages with genuine English demand are mirrored: hub, three services
// and Stockholm. Each entry maps to its Swedish counterpart for hreflang.

import type { PhotoCategory } from "@/content/photo-gallery";

export interface EnPhotoPage {
  slug: string; // "" for the hub
  path: string;
  svPath: string;
  crumb: string;
  eyebrow: string;
  metaTitle: string;
  description: string;
  h1: string;
  lede: string;
  question: string;
  quickAnswer: string;
  serviceType: string;
  galleryHeading: string;
  gallery: PhotoCategory | "alla";
  knowsAbout: string[];
  bullets: { title: string; body: string }[];
  pricing?: {
    intro: string;
    rows: { format: string; scope: string; price: string; lead?: string }[];
    footnote: string;
  };
  process?: { label: string; body: string; duration?: string }[];
  sections: { id: string; heading: string; paragraphs: string[] }[];
  faq: { q: string; a: string }[];
  related: { label: string; to: string }[];
}

const RELATED_ALL = [
  { label: "Corporate photography", to: "/en/photography" },
  { label: "Corporate portraits & headshots", to: "/en/photography/corporate-portraits" },
  { label: "Product photography", to: "/en/photography/product-photography" },
  { label: "Event photography", to: "/en/photography/event-photography" },
  { label: "Photographer in Stockholm", to: "/en/photography/stockholm" },
  { label: "Film production", to: "/en/film-production" },
];

const related = (exclude: string) => RELATED_ALL.filter((r) => r.to !== exclude);

export const enPhotoPages: EnPhotoPage[] = [
  {
    slug: "",
    path: "/en/photography",
    svPath: "/foto",
    crumb: "Photography",
    eyebrow: "Fenny Photography",
    metaTitle: "Corporate photography in Sweden — Fenny Photography",
    description:
      "English-speaking corporate photographer in Sweden. Headshots, product, campaign and event photography — studio or your office. Quote within one working day.",
    h1: "Corporate photography for companies in Sweden",
    lede:
      "Fenny Photography shoots portraits, product imagery, campaigns and events for Swedish and international companies operating in Sweden. Studio in Stockholm, on-location anywhere in the country, and a photographer who works in English.",
    question: "What does a corporate photographer in Sweden do?",
    quickAnswer:
      "A corporate photographer produces the images a company needs commercially: headshots for the website and LinkedIn, environmental portraits, product photography for e-commerce, campaign imagery and event coverage. Fenny works in English, shoots in studio or at your premises across Sweden, and delivers retouched, web- and print-ready files. Stated by Fenny, reviewed 2026.",
    serviceType: "Corporate photography",
    galleryHeading: "Selected work",
    gallery: "alla",
    knowsAbout: [
      "Corporate photography",
      "Headshots",
      "Product photography",
      "Event photography",
      "Campaign photography",
      "Studio lighting",
    ],
    bullets: [
      { title: "One photographer, one look", body: "The same photographer, the same lighting and the same grade across portraits, product and campaign — so your image bank stays consistent." },
      { title: "English-speaking crew", body: "Briefing, direction on set and delivery all happen in English. Useful when the marketing team sits outside Sweden." },
      { title: "Fast delivery", body: "Preview gallery within 2 working days, retouched selects within 5. Event coverage can be delivered the same evening." },
      { title: "Clear usage rights", body: "You get unlimited internal and marketing usage in the quote. No per-channel licensing surprises." },
    ],
    pricing: {
      intro:
        "Indicative 2026 pricing excluding VAT. Every assignment is quoted individually — these are the ranges most requests land in.",
      rows: [
        { format: "Headshot session", scope: "Up to 10 people, one location, 1 retouched image each", price: "SEK 14,000–22,000", lead: "1 day on site" },
        { format: "Half-day on location", scope: "Portraits and environment, 4 hours, 25 retouched images", price: "SEK 16,000–24,000", lead: "5 working days" },
        { format: "Full-day production", scope: "Portraits, environment and campaign, 8 hours, 50 retouched images", price: "SEK 26,000–38,000", lead: "5–7 working days" },
        { format: "Product photography", scope: "Studio, per product with 3 angles", price: "SEK 350–900 / product", lead: "3–7 working days" },
        { format: "Event coverage", scope: "4 hours, 60–100 edited images", price: "SEK 15,000–24,000", lead: "Same evening or 2 days" },
      ],
      footnote:
        "Travel outside Stockholm, Gothenburg and Malmö is invoiced at cost. Prices reviewed 2026 and stated by Fenny.",
    },
    process: [
      { label: "Brief", body: "A 20-minute call about purpose, channels, number of people and where the images will be used.", duration: "Day 0" },
      { label: "Quote", body: "Fixed price, scope and shoot schedule in writing.", duration: "Within 1 working day" },
      { label: "Preparation", body: "Shot list, wardrobe guidance for the people involved and a location plan.", duration: "Before the shoot" },
      { label: "Shoot", body: "Studio or on location. We keep a schedule so nobody waits longer than needed.", duration: "Shoot day" },
      { label: "Selection and retouch", body: "Preview gallery, you pick the selects, we retouch and deliver in web and print resolution.", duration: "2–5 working days" },
    ],
    sections: [
      {
        id: "what-we-shoot",
        heading: "What we shoot",
        paragraphs: [
          "Headshots and environmental portraits for websites, LinkedIn and press rooms. Product photography for e-commerce and catalogues. Campaign imagery tied to a visual direction. Conference, trade fair and internal event coverage.",
          "Most clients start with portraits and then expand into product or campaign work once the visual direction is established. Because the same photographer covers all of it, the image bank stays coherent instead of drifting between suppliers.",
        ],
      },
      {
        id: "how-we-work",
        heading: "How we work with international teams",
        paragraphs: [
          "Fenny works with Swedish subsidiaries of international groups where brand guidelines, approvals and asset delivery run in English. We take the brand book, translate it into a lighting and framing approach, and deliver files named and sized to your DAM requirements.",
          "If you also need moving images, the same shoot day can produce both stills and video — see film production for how that is planned.",
        ],
      },
      {
        id: "coverage",
        heading: "Where we shoot",
        paragraphs: [
          "Studio in Stockholm and on-location work across Sweden: Gothenburg, Malmö, Uppsala, Västerås, Linköping, Örebro, Helsingborg, Lund, Umeå and Luleå among others. Travel is planned so a single trip covers as many locations as possible.",
        ],
      },
    ],
    faq: [
      { q: "Do you work in English?", a: "Yes. Briefing, on-set direction, image selection and delivery all run in English." },
      { q: "How much does a corporate photographer cost in Sweden?", a: "A half-day on location is typically SEK 16,000–24,000 and a full day SEK 26,000–38,000 excluding VAT, including retouching and usage rights. Product photography is quoted per product from around SEK 350." },
      { q: "How quickly do we get the images?", a: "A preview gallery arrives within two working days and retouched selects within five. Event coverage can be delivered the same evening when needed." },
      { q: "Who owns the images?", a: "You get unlimited usage rights for marketing and internal use. Fenny retains the right to show the work in its own portfolio unless you ask otherwise." },
      { q: "Can you shoot at our office?", a: "Yes. We bring lighting, backdrops and everything needed to build a temporary studio in a meeting room or open space." },
      { q: "Can we combine photo and video in one day?", a: "Yes. A combined day is the most cost-effective way to produce both stills and film, and it keeps the look consistent across formats." },
    ],
    related: related("/en/photography"),
  },
  {
    slug: "corporate-portraits",
    path: "/en/photography/corporate-portraits",
    svPath: "/foto/foretagsportratt",
    crumb: "Corporate portraits",
    eyebrow: "Portraits & headshots",
    metaTitle: "Corporate portraits & headshots in Sweden | Fenny",
    description:
      "Consistent corporate headshots for websites, LinkedIn and press rooms. Studio or your office anywhere in Sweden, retouched delivery in 5 working days.",
    h1: "Corporate portraits and headshots",
    lede:
      "One lighting setup, one grade and one photographer — so the whole team looks like it belongs to the same company, whether people are photographed in Stockholm or at a subsidiary in Malmö.",
    question: "What does a corporate headshot session cost in Sweden?",
    quickAnswer:
      "A headshot session for up to ten people at one location costs SEK 14,000–22,000 excluding VAT and includes lighting setup, direction, one retouched image per person and usage rights. Larger groups are quoted per day; roughly 30–40 people fit into a full studio day. Stated by Fenny, reviewed 2026.",
    serviceType: "Corporate portrait photography",
    galleryHeading: "Portraits",
    gallery: "portratt",
    knowsAbout: ["Headshots", "Corporate portraits", "LinkedIn portraits", "Studio lighting", "Retouching"],
    bullets: [
      { title: "Repeatable setup", body: "The lighting is documented, so new hires photographed a year later match the originals exactly." },
      { title: "Calm direction", body: "Most people dislike being photographed. Two minutes of direction per person is usually enough to get a natural result." },
      { title: "Two crops delivered", body: "A tight headshot for LinkedIn and a wider frame for the website, from the same frame." },
      { title: "Honest retouching", body: "Skin and stray hairs cleaned up. Nobody is reshaped." },
    ],
    pricing: {
      intro: "Indicative 2026 pricing excluding VAT.",
      rows: [
        { format: "Small team", scope: "Up to 10 people, one location, 1 retouched image each", price: "SEK 14,000–22,000", lead: "5 working days" },
        { format: "Full studio day", scope: "30–40 people, 1 retouched image each", price: "SEK 26,000–34,000", lead: "5–7 working days" },
        { format: "Single executive portrait", scope: "Studio or office, 3 retouched images", price: "SEK 6,000–9,000", lead: "3 working days" },
        { format: "Extra retouched image", scope: "Per additional select", price: "SEK 250–400", lead: "—" },
      ],
      footnote: "Travel outside Stockholm, Gothenburg and Malmö invoiced at cost. Reviewed 2026.",
    },
    process: [
      { label: "Schedule", body: "We build a slot list so each person spends 5–8 minutes in front of the camera.", duration: "Before the day" },
      { label: "Setup", body: "One hour to build lighting and backdrop in a meeting room or the studio.", duration: "Shoot day" },
      { label: "Shoot", body: "Direction, frame check and two crops per person.", duration: "Shoot day" },
      { label: "Selection", body: "Preview gallery where each person or HR picks the select.", duration: "2 working days" },
      { label: "Delivery", body: "Retouched files in web and print resolution, named per person.", duration: "5 working days" },
    ],
    sections: [
      {
        id: "consistency",
        heading: "Why consistency matters more than the individual image",
        paragraphs: [
          "A management page where five portraits were shot by four different photographers reads as sloppy long before anyone can explain why. Same background, same eye height, same colour temperature — that is what makes a team page look deliberate.",
          "We document the setup after every session: light positions, modifiers, distances and grade. When you hire someone six months later, that person can be added without the difference showing.",
        ],
      },
      {
        id: "preparing",
        heading: "How to prepare the team",
        paragraphs: [
          "Send a short note the week before: solid colours rather than tight patterns, bring a second top as an alternative, and skip the brand-new haircut on the day itself. That is essentially all the preparation required.",
          "Book people in a schedule rather than announcing a drop-in window. It keeps the day short and stops the queue that makes everyone tense.",
        ],
      },
    ],
    faq: [
      { q: "How many people fit into one day?", a: "Around 30–40 people with one retouched image each, if the schedule is set in advance." },
      { q: "Do you shoot on white or a dark background?", a: "Both are available. We usually recommend picking one and staying with it across the whole organisation." },
      { q: "Can new employees be added later?", a: "Yes. The lighting setup is documented so later sessions match the original ones." },
      { q: "Do you retouch the images?", a: "Yes, one retouched image per person is included. Additional selects are SEK 250–400 each." },
      { q: "Can you shoot at our office?", a: "Yes. We need roughly 3 × 4 metres of floor space and one hour to set up." },
    ],
    related: related("/en/photography/corporate-portraits"),
  },
  {
    slug: "product-photography",
    path: "/en/photography/product-photography",
    svPath: "/foto/produktfoto",
    crumb: "Product photography",
    eyebrow: "Product",
    metaTitle: "Product photography in Sweden — e-commerce | Fenny",
    description:
      "Product photography for e-commerce, catalogue and advertising. Consistent packshots and styled lifestyle imagery, quoted per product. Studio in Stockholm.",
    h1: "Product photography for e-commerce and campaigns",
    lede:
      "Packshots that behave identically across a whole catalogue, plus styled imagery when the product needs a context. Quoted per product so the budget follows the assortment.",
    question: "What does product photography cost per product in Sweden?",
    quickAnswer:
      "Product photography in Sweden is normally quoted per product: SEK 350–900 per item for three angles on a white background, and SEK 1,200–2,500 per styled lifestyle image. Volume lowers the per-unit price; complex reflective or transparent products raise it. Stated by Fenny, reviewed 2026.",
    serviceType: "Product photography",
    galleryHeading: "Product and studio work",
    gallery: "studio",
    knowsAbout: ["Product photography", "Packshots", "E-commerce imagery", "Studio lighting", "Clipping paths"],
    bullets: [
      { title: "Catalogue consistency", body: "Same distance, same angle, same light across every SKU — so the grid on the category page looks calm." },
      { title: "Clipping included", body: "Files delivered on transparent background plus flattened white, ready for the platform." },
      { title: "Per-product pricing", body: "You know the cost before the shipment arrives, and volume brings the unit price down." },
      { title: "Lifestyle when needed", body: "Styled context images for campaign and social, shot in the same session." },
    ],
    pricing: {
      intro: "Indicative 2026 pricing excluding VAT, quoted per product.",
      rows: [
        { format: "Packshot", scope: "White background, 3 angles, clipping path", price: "SEK 350–900 / product", lead: "3–7 working days" },
        { format: "Volume packshot", scope: "50+ products, 2 angles each", price: "SEK 250–450 / product", lead: "7–10 working days" },
        { format: "Lifestyle image", scope: "Styled scene, props and set", price: "SEK 1,200–2,500 / image", lead: "5–10 working days" },
        { format: "Studio day", scope: "8 hours, mixed packshot and lifestyle", price: "SEK 22,000–32,000", lead: "5 working days" },
      ],
      footnote: "Reflective, transparent or assembled products are quoted individually. Reviewed 2026.",
    },
    process: [
      { label: "Assortment review", body: "We go through the product list and group items by lighting need.", duration: "Day 0" },
      { label: "Shipping", body: "Products are sent to the studio or collected in Stockholm.", duration: "Before the shoot" },
      { label: "Reference shot", body: "One approved product sets the standard for the rest of the batch.", duration: "Shoot day 1" },
      { label: "Production", body: "The batch is shot against the approved reference.", duration: "1–3 days" },
      { label: "Delivery", body: "Clipped files in the naming convention and sizes your platform expects.", duration: "3–7 working days" },
    ],
    sections: [
      {
        id: "consistency",
        heading: "The reference shot decides everything",
        paragraphs: [
          "We shoot one product, send it for approval and only then run the batch. It costs an hour up front and removes the risk of reshooting two hundred items because the shadow was too heavy.",
          "For e-commerce the important thing is not that each image is beautiful in isolation but that the product occupies the same proportion of the frame every time. That is what makes a category page feel professional.",
        ],
      },
      {
        id: "files",
        heading: "File delivery",
        paragraphs: [
          "Delivery follows your platform: PNG with transparency, JPG on white, plus a high-resolution TIFF for print if the catalogue needs it. Naming follows your SKU convention so import is a drag-and-drop rather than a manual job.",
        ],
      },
    ],
    faq: [
      { q: "How is product photography priced?", a: "Per product. SEK 350–900 for three angles on white, dropping towards SEK 250 at higher volumes." },
      { q: "Do you provide clipping paths?", a: "Yes, transparent-background files are included in the packshot price." },
      { q: "How do we get the products to you?", a: "Ship them to the studio in Stockholm or have them collected locally. We return them after the shoot." },
      { q: "Can you shoot reflective or glass products?", a: "Yes, but they take longer to light and are quoted individually." },
      { q: "How long does a catalogue take?", a: "A batch of 50 products is normally shot in one to two days and delivered within a week." },
    ],
    related: related("/en/photography/product-photography"),
  },
  {
    slug: "event-photography",
    path: "/en/photography/event-photography",
    svPath: "/foto/eventfoto",
    crumb: "Event photography",
    eyebrow: "Events",
    metaTitle: "Event photography in Sweden — conferences | Fenny",
    description:
      "Conference, trade fair and event photography across Sweden. Discreet coverage, a first selection delivered the same evening, English-speaking crew.",
    h1: "Event photography for conferences and corporate events",
    lede:
      "Coverage that documents the programme, the people and the room without turning the event into a photo shoot — and a first selection in your hands before the day is over.",
    question: "How fast can event photos be delivered?",
    quickAnswer:
      "Fenny delivers a first selection of 15–25 edited event images the same evening, and the full set of 60–100 images within two working days. Same-evening delivery is included in the quote when the event is being covered on social media. Stated by Fenny, reviewed 2026.",
    serviceType: "Event photography",
    galleryHeading: "Event and campaign work",
    gallery: "kampanj",
    knowsAbout: ["Event photography", "Conference photography", "Trade fair photography", "Available-light shooting"],
    bullets: [
      { title: "Same-evening selection", body: "15–25 edited images delivered before you go to bed, so the recap post goes out while the event is fresh." },
      { title: "Discreet on the floor", body: "Available light where possible, minimal gear, no interruption of speakers or conversations." },
      { title: "Shot list agreed", body: "Keynote, sponsor signage, networking, details — we agree what must exist before the doors open." },
      { title: "Multi-day coverage", body: "Trade fairs and conferences covered across several days with consistent look." },
    ],
    pricing: {
      intro: "Indicative 2026 pricing excluding VAT.",
      rows: [
        { format: "Half-day event", scope: "4 hours, 60–100 edited images", price: "SEK 15,000–24,000", lead: "Same evening + 2 days" },
        { format: "Full-day event", scope: "8 hours, 120–200 edited images", price: "SEK 24,000–34,000", lead: "Same evening + 2 days" },
        { format: "Multi-day fair", scope: "Per additional day", price: "SEK 18,000–26,000 / day", lead: "2 working days" },
        { format: "Second photographer", scope: "Parallel tracks or large venues", price: "SEK 12,000–18,000 / day", lead: "—" },
      ],
      footnote: "Travel and accommodation outside the three metros invoiced at cost. Reviewed 2026.",
    },
    process: [
      { label: "Run-through", body: "We go through the programme and agree the must-have shots.", duration: "Week before" },
      { label: "Recce", body: "Arrival an hour early to check light, stage and sightlines.", duration: "Event day" },
      { label: "Coverage", body: "Programme, people and details, shot to the agreed list.", duration: "Event day" },
      { label: "First selection", body: "15–25 edited images delivered the same evening.", duration: "Event evening" },
      { label: "Full set", body: "The complete edited gallery.", duration: "2 working days" },
    ],
    sections: [
      {
        id: "what-to-brief",
        heading: "What to brief the photographer on",
        paragraphs: [
          "Three things decide whether event photography works: who must be photographed by name, which sponsor or brand elements must appear, and what the images will be used for afterwards. Everything else can be handled on the day.",
          "If the images are for recruitment or employer branding, say so — that changes what gets shot. Rooms full of people looking at slides are not useful for that purpose; conversations and reactions are.",
        ],
      },
      {
        id: "consent",
        heading: "Consent and GDPR",
        paragraphs: [
          "Attendees should be informed that photography takes place, normally in the registration email and with signage at the entrance. We honour opt-outs on the day and can supply a simple consent wording if you do not already have one.",
        ],
      },
    ],
    faq: [
      { q: "How quickly do we get event images?", a: "A first selection of 15–25 edited images the same evening, and the full set within two working days." },
      { q: "How many images do we get?", a: "60–100 for a half-day and 120–200 for a full day, all edited." },
      { q: "Can you cover parallel sessions?", a: "Yes, with a second photographer at SEK 12,000–18,000 per day." },
      { q: "Do you handle consent?", a: "We follow your consent process, honour opt-outs and can provide standard wording for the registration email." },
      { q: "Can you also film the event?", a: "Yes. Fenny is a film production company first, so an aftermovie can be produced from the same event." },
    ],
    related: related("/en/photography/event-photography"),
  },
  {
    slug: "stockholm",
    path: "/en/photography/stockholm",
    svPath: "/foto/stockholm",
    crumb: "Stockholm",
    eyebrow: "Stockholm",
    metaTitle: "English-speaking photographer in Stockholm | Fenny",
    description:
      "Corporate photographer in Stockholm working in English. Headshots, product and event photography in studio or at your office. Quote within one working day.",
    h1: "English-speaking corporate photographer in Stockholm",
    lede:
      "Studio in Stockholm and on-location work across the region — from single executive portraits to full campaign days for international teams based in the city.",
    question: "Is there an English-speaking corporate photographer in Stockholm?",
    quickAnswer:
      "Yes. Fenny Photography operates a studio in Stockholm and works entirely in English, covering the inner city, Solna, Kista, Sickla, Södermalm and the Arlanda area. A half-day on location costs SEK 16,000–24,000 excluding VAT with delivery in five working days. Stated by Fenny, reviewed 2026.",
    serviceType: "Corporate photography in Stockholm",
    galleryHeading: "Work from Stockholm",
    gallery: "alla",
    knowsAbout: ["Photographer Stockholm", "Headshots Stockholm", "Product photography Stockholm", "Event photography Stockholm"],
    bullets: [
      { title: "Studio in the city", body: "A controlled space for portraits and product work, twenty minutes from most offices in central Stockholm." },
      { title: "On location", body: "We build a temporary studio in your meeting room when moving thirty people across town is not realistic." },
      { title: "Short notice", body: "Single portraits can often be booked within the week." },
      { title: "Whole region covered", body: "Solna, Kista, Sickla, Södermalm, Nacka, Täby and the Arlanda corridor at no extra travel cost." },
    ],
    process: [
      { label: "Call", body: "Twenty minutes on purpose, people and channels.", duration: "Day 0" },
      { label: "Quote", body: "Fixed price and a proposed date.", duration: "Within 1 working day" },
      { label: "Shoot", body: "Studio in Stockholm or at your office.", duration: "Shoot day" },
      { label: "Delivery", body: "Preview gallery, selection, retouch and final files.", duration: "5 working days" },
    ],
    sections: [
      {
        id: "areas",
        heading: "Areas we cover",
        paragraphs: [
          "Central Stockholm, Solna, Kista, Sickla, Södermalm, Nacka, Täby, Bromma and the Arlanda area are covered without travel charges. Uppsala and Västerås are within an hour and frequently combined into the same production week.",
        ],
      },
      {
        id: "international",
        heading: "For international teams in Stockholm",
        paragraphs: [
          "A large share of the assignments come from Swedish offices of international groups where the brand guidelines, the approval chain and the asset library all operate in English. We work directly against those guidelines and deliver files that fit the group DAM.",
        ],
      },
    ],
    faq: [
      { q: "Do you have a studio in Stockholm?", a: "Yes, a studio in Stockholm for portraits and product photography, plus on-location work across the region." },
      { q: "What does a photographer in Stockholm cost?", a: "A half-day is SEK 16,000–24,000 and a full day SEK 26,000–38,000 excluding VAT, including retouching and usage rights." },
      { q: "How quickly can you shoot?", a: "Single portraits can often be booked within the week; larger productions typically two to three weeks out." },
      { q: "Do you charge travel within Stockholm?", a: "No. The inner city, Solna, Kista, Sickla, Nacka, Täby, Bromma and Arlanda are included." },
    ],
    related: related("/en/photography/stockholm"),
  },
  {
    slug: "gothenburg",
    path: "/en/photography/gothenburg",
    svPath: "/foto/goteborg",
    crumb: "Gothenburg",
    eyebrow: "Gothenburg",
    metaTitle: "English-speaking photographer in Gothenburg | Fenny",
    description:
      "Corporate photographer in Gothenburg working in English. Headshots, product and event photography at your office or in studio. Quote within one working day.",
    h1: "English-speaking corporate photographer in Gothenburg",
    lede:
      "On-location photography across Gothenburg and the western region — portraits for whole departments, product work for e-commerce and coverage of conferences and trade fairs.",
    question: "Is there an English-speaking corporate photographer in Gothenburg?",
    quickAnswer:
      "Yes. Fenny Photography shoots in Gothenburg in English, covering the inner city, Lindholmen, Mölndal, Partille, Kungsbacka and the Landvetter area. A half-day on location costs SEK 16,000–24,000 excluding VAT with delivery in five working days. Stated by Fenny, reviewed 2026.",
    serviceType: "Corporate photography in Gothenburg",
    galleryHeading: "Work from Gothenburg",
    gallery: "alla",
    knowsAbout: [
      "Photographer Gothenburg",
      "Headshots Gothenburg",
      "Product photography Gothenburg",
      "Event photography Gothenburg",
    ],
    bullets: [
      { title: "Mobile studio", body: "We build a temporary studio in a meeting room, so an entire department can be photographed without leaving the building." },
      { title: "Industry experience", body: "Automotive, logistics and manufacturing environments where safety routines and shift schedules govern the shoot day." },
      { title: "One trip, many locations", body: "Offices in Gothenburg, Mölndal and Borås are planned into a single production trip." },
      { title: "Same look as Stockholm", body: "The same lighting and grade as the rest of your image bank, regardless of city." },
    ],
    process: [
      { label: "Call", body: "Twenty minutes on purpose, people and channels.", duration: "Day 0" },
      { label: "Quote", body: "Fixed price and a proposed date.", duration: "Within 1 working day" },
      { label: "Shoot", body: "At your premises in Gothenburg, or in a rented studio in the city.", duration: "Shoot day" },
      { label: "Delivery", body: "Preview gallery, selection, retouch and final files.", duration: "5 working days" },
    ],
    sections: [
      {
        id: "areas",
        heading: "Areas we cover",
        paragraphs: [
          "Central Gothenburg, Lindholmen, Gårda, Mölndal, Partille, Kungsbacka, Kungälv and the Landvetter corridor. Borås, Trollhättan and Varberg are usually combined into the same production trip to keep travel off the invoice.",
        ],
      },
      {
        id: "industry",
        heading: "Shooting in industrial environments",
        paragraphs: [
          "A large part of the work in the region takes place in production halls, ports and logistics facilities. We arrive with safety gear, plan around shift changes and work with mixed light so machines and people read clearly in the same frame.",
          "Safety inductions, escorts and photo restrictions are cleared before the shoot day so no time is lost on site.",
        ],
      },
    ],
    faq: [
      { q: "Do you travel to Gothenburg from Stockholm?", a: "Yes. Travel is included when the shoot fills at least a half-day; shorter assignments are quoted with travel at cost." },
      { q: "What does a photographer in Gothenburg cost?", a: "A half-day is SEK 16,000–24,000 and a full day SEK 26,000–38,000 excluding VAT, including retouching and usage rights." },
      { q: "Can you shoot in a factory or port?", a: "Yes. We hold the required safety gear and plan the shoot around shift schedules and access clearance." },
      { q: "Can film be shot the same day?", a: "Yes. A combined photo and film day is the most cost-effective option and keeps the look consistent." },
    ],
    related: related("/en/photography/gothenburg"),
  },
  {
    slug: "malmo",
    path: "/en/photography/malmo",
    svPath: "/foto/malmo",
    crumb: "Malmö",
    eyebrow: "Malmö",
    metaTitle: "English-speaking photographer in Malmö — Fenny Photography",
    description:
      "Corporate photographer in Malmö working in English. Headshots, product and event photography in Malmö, Lund or Copenhagen. Quote within one working day.",
    h1: "English-speaking corporate photographer in Malmö",
    lede:
      "On-location photography across Malmö, Lund and the Öresund region — portraits, product photography and event coverage for teams that work in English across two countries.",
    question: "Is there an English-speaking corporate photographer in Malmö?",
    quickAnswer:
      "Yes. Fenny Photography shoots in Malmö in English, covering the inner city, Hyllie, Västra Hamnen, Lund, Helsingborg and Copenhagen. A half-day on location costs SEK 16,000–24,000 excluding VAT with delivery in five working days. Stated by Fenny, reviewed 2026.",
    serviceType: "Corporate photography in Malmö",
    galleryHeading: "Work from Malmö",
    gallery: "alla",
    knowsAbout: [
      "Photographer Malmö",
      "Headshots Malmö",
      "Product photography Malmö",
      "Event photography Malmö",
    ],
    bullets: [
      { title: "Öresund coverage", body: "Malmö, Lund, Helsingborg and Copenhagen in the same production trip when the group has offices on both sides." },
      { title: "Research and life science", body: "Lab and clean-room environments where access, clothing and equipment handling are agreed in advance." },
      { title: "Mobile studio", body: "A temporary studio in your meeting room means the whole team is photographed in one day." },
      { title: "Consistent image bank", body: "The same lighting and grade as portraits shot in Stockholm or Gothenburg." },
    ],
    process: [
      { label: "Call", body: "Twenty minutes on purpose, people and channels.", duration: "Day 0" },
      { label: "Quote", body: "Fixed price and a proposed date.", duration: "Within 1 working day" },
      { label: "Shoot", body: "At your premises in Malmö or Lund, or in a rented studio in the city.", duration: "Shoot day" },
      { label: "Delivery", body: "Preview gallery, selection, retouch and final files.", duration: "5 working days" },
    ],
    sections: [
      {
        id: "areas",
        heading: "Areas we cover",
        paragraphs: [
          "Central Malmö, Västra Hamnen, Hyllie, Limhamn, Lund, Ideon, Staffanstorp, Lomma and Helsingborg. Assignments in Copenhagen are quoted with the bridge crossing included and are usually planned as part of the same trip.",
        ],
      },
      {
        id: "life-science",
        heading: "Photography in labs and clean rooms",
        paragraphs: [
          "Research environments in Lund and Malmö require agreements on access, clothing and how equipment is cleaned before entry. We plan those steps with your site contact and shoot with lighting that does not disturb ongoing work.",
          "Where confidentiality applies, screens, samples and documents are covered or removed before the frame is shot, and the image selection is reviewed with you before publication.",
        ],
      },
    ],
    faq: [
      { q: "Do you shoot in Copenhagen as well?", a: "Yes. Assignments across the bridge are quoted with the crossing included and are often combined with a shoot day in Malmö or Lund." },
      { q: "What does a photographer in Malmö cost?", a: "A half-day is SEK 16,000–24,000 and a full day SEK 26,000–38,000 excluding VAT, including retouching and usage rights." },
      { q: "Can you photograph in a laboratory?", a: "Yes. Access, protective clothing and equipment handling are agreed with your site contact before the shoot day." },
      { q: "Do you cover Lund and Helsingborg?", a: "Yes, both are included in the Malmö region and can be shot on the same trip." },
    ],
    related: related("/en/photography/malmo"),
  },
];

export const EN_PHOTO_PATHS = enPhotoPages.map((p) => p.path);
